import { useEffect, useMemo, useState } from 'react';
import { X, Plus } from 'lucide-react';
import { useStore } from '../../store/useStore';
import { api } from '../../api/client';
import { STAGES, PLATFORMS } from '../../data/mockData';

const PLATFORM_STYLES = {
  telegram: { bg: '#E3F4FC', color: '#0C6B96' },
  whatsapp: { bg: '#E1F5E9', color: '#1A6B36' },
  teams: { bg: '#EDEBFF', color: '#3730A3' },
  email: { bg: '#FDECEA', color: '#9C1B14' },
  facebook: { bg: '#E7F0FD', color: '#0C4FA8' },
  instagram: { bg: '#FDEAF1', color: '#8B0D3A' },
};

const TAG_COLORS = ['tag-blue', 'tag-green', 'tag-amber', 'tag-gray'];

const stripFlag = (value) => value.replace(/^[\u{1F1E6}-\u{1F1FF}]{2}\s*/u, '');

export default function CrmPanel() {
  const { getActiveContact, getActiveConv, updateContact, addReminder, removeReminder, addOrder, removeOrder, showCrm, reminderComposerOpen, closeReminderComposer } = useStore();
  const conv = getActiveConv();
  const contact = getActiveContact();
  const [addingReminder, setAddingReminder] = useState(false);
  const [addingOrder, setAddingOrder] = useState(false);
  const [reminderText, setReminderText] = useState('');
  const [reminderTime, setReminderTime] = useState('');
  const [orderTitle, setOrderTitle] = useState('');
  const [orderAmount, setOrderAmount] = useState('');
  const [orderDate, setOrderDate] = useState('');
  const [draftName, setDraftName] = useState('');
  const [draftCompany, setDraftCompany] = useState('');
  const [draftCountry, setDraftCountry] = useState('');
  const [draftEmail, setDraftEmail] = useState('');
  const [draftWhatsapp, setDraftWhatsapp] = useState('');
  const [draftTeams, setDraftTeams] = useState('');
  const [draftNotes, setDraftNotes] = useState('');
  const [addingTag, setAddingTag] = useState(false);
  const [tagText, setTagText] = useState('');
  const [assignees, setAssignees] = useState([]);

  useEffect(() => {
    let active = true;
    api.listPermissionUsers()
      .then(items => {
        if (!active) return;
        setAssignees(items
          .filter(item => item.status === '启用')
          .map(item => ({
            id: String(item.id),
            name: item.displayName || item.username,
            initials: (item.displayName || item.username || '?').slice(0, 2).toUpperCase(),
          })));
      })
      .catch(() => {
        if (!active) return;
        setAssignees([]);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    setDraftName(contact?.name || '');
    setDraftCompany(contact?.company || '');
    setDraftCountry(stripFlag(contact?.country || ''));
    setDraftEmail(contact?.email || '');
    setDraftWhatsapp(contact?.whatsapp || '');
    setDraftTeams(contact?.teams || '');
    setDraftNotes(contact?.notes || '');
  }, [contact?.id, contact?.name, contact?.company, contact?.country, contact?.email, contact?.whatsapp, contact?.teams, contact?.notes]);

  useEffect(() => {
    if (reminderComposerOpen) {
      setAddingReminder(true);
    }
  }, [reminderComposerOpen]);

  const assigneeOptions = useMemo(() => {
    if (!contact) return assignees;
    if (!contact.assignee?.id || assignees.some(item => item.id === contact.assignee.id)) {
      return assignees;
    }
    return [
      {
        id: contact.assignee.id,
        name: contact.assignee.name,
        initials: contact.assignee.initials,
      },
      ...assignees,
    ];
  }, [assignees, contact]);

  if (!showCrm || !contact) return null;

  const handleStageClick = async (idx) => {
    if (idx === contact.stage) return;
    await updateContact(contact.id, { stage: idx });
  };

  const handleNotesChange = (e) => {
    setDraftNotes(e.target.value);
  };

  const handleNotesBlur = async () => {
    if (draftNotes !== contact.notes) {
      await updateContact(contact.id, { notes: draftNotes });
    }
  };

  const handleProfileBlur = async () => {
    const name = draftName.trim();
    const company = draftCompany.trim();
    const countryName = stripFlag(draftCountry).trim();
    const email = draftEmail.trim();
    const whatsapp = draftWhatsapp.trim();
    const teams = draftTeams.trim();
    const currentCountryName = stripFlag(contact.country || '').trim();
    const updates = {};
    if (!name) {
      setDraftName(contact.name || '');
    } else if (name !== contact.name) {
      updates.name = name;
    }
    if (company !== (contact.company || '')) updates.company = company;
    if (countryName !== currentCountryName) updates.countryName = countryName;
    if (email !== (contact.email || '')) updates.email = email;
    if (whatsapp !== (contact.whatsapp || '')) updates.whatsapp = whatsapp;
    if (teams !== (contact.teams || '')) updates.teams = teams;
    if (Object.keys(updates).length > 0) {
      await updateContact(contact.id, updates);
    }
  };

  const handleAddReminder = async () => {
    if (!reminderText.trim()) return;
    await addReminder(contact.id, { text: reminderText.trim(), time: reminderTime || '待定' });
    setReminderText(''); setReminderTime(''); setAddingReminder(false);
    closeReminderComposer();
  };

  const handleAddTag = async () => {
    const nextTag = tagText.trim();
    if (!nextTag || contact.tags.includes(nextTag)) return;
    await updateContact(contact.id, { tags: [...contact.tags, nextTag] });
    setTagText('');
    setAddingTag(false);
  };

  const handleRemoveTag = async (tag) => {
    await updateContact(contact.id, { tags: contact.tags.filter(item => item !== tag) });
  };

  const handleAddOrder = async () => {
    if (!orderTitle.trim()) return;
    await addOrder(contact.id, {
      title: orderTitle.trim(),
      amount: orderAmount.trim(),
      date: orderDate.trim(),
    });
    setOrderTitle('');
    setOrderAmount('');
    setOrderDate('');
    setAddingOrder(false);
  };

  return (
    <div className="crm-panel">
      {/* Customer Info */}
      <div className="crm-section">
        <div className="crm-section-title">客户信息</div>
        <div className="crm-field">
          <div className="crm-field-label">姓名</div>
          <input
            className="crm-field-input"
            value={draftName}
            onChange={e => setDraftName(e.target.value)}
            onBlur={handleProfileBlur}
          />
        </div>
        <div className="crm-field">
          <div className="crm-field-label">公司</div>
          <input
            className="crm-field-input"
            value={draftCompany}
            onChange={e => setDraftCompany(e.target.value)}
            onBlur={handleProfileBlur}
          />
        </div>
        <div className="crm-field">
          <div className="crm-field-label">国家 / 地区</div>
          <input
            className="crm-field-input"
            value={draftCountry}
            onChange={e => setDraftCountry(e.target.value)}
            onBlur={handleProfileBlur}
          />
        </div>
        <div className="crm-field">
          <div className="crm-field-label">邮箱</div>
          <input
            className="crm-field-input"
            type="email"
            value={draftEmail}
            onChange={e => setDraftEmail(e.target.value)}
            onBlur={handleProfileBlur}
          />
        </div>
        <div className="crm-field">
          <div className="crm-field-label">WhatsApp</div>
          <input
            className="crm-field-input"
            value={draftWhatsapp}
            placeholder="国家码+手机号，如 8613800000000"
            onChange={e => setDraftWhatsapp(e.target.value)}
            onBlur={handleProfileBlur}
          />
        </div>
        <div className="crm-field">
          <div className="crm-field-label">Teams chatId</div>
          <input
            className="crm-field-input"
            value={draftTeams}
            placeholder="Microsoft Teams chat id"
            onChange={e => setDraftTeams(e.target.value)}
            onBlur={handleProfileBlur}
          />
        </div>
        <div className="crm-field">
          <div className="crm-field-label">联系渠道</div>
          <div className="platform-tags">
            {contact.platforms.map(p => {
              const ps = PLATFORM_STYLES[p] || {};
              const pl = PLATFORMS[p];
              return (
                <span key={p} className="p-tag" style={{ background: ps.bg, color: ps.color }}>
                  {pl?.name || p}
                </span>
              );
            })}
          </div>
        </div>
        <div className="crm-field">
          <div className="crm-field-label">负责人</div>
          <div className="assignee-row">
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div className="mini-avatar">{contact.assignee.initials}</div>
              <span className="assignee-name">{contact.assignee.name}</span>
            </div>
            <select
              className="customer-platform-select"
              value={contact.assignee?.id || ''}
              onChange={async (event) => {
                const next = assigneeOptions.find(item => item.id === event.target.value);
                if (!next || next.id === contact.assignee?.id) return;
                await updateContact(contact.id, { assignee: next });
              }}
            >
              {assigneeOptions.map(item => (
                <option key={item.id} value={item.id}>{item.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Stage */}
      <div className="crm-section">
        <div className="crm-section-title">跟进阶段</div>
        <div className="stage-labels">
          {STAGES.map((s, i) => (
            <span key={i} style={{ color: i === contact.stage ? 'var(--c-blue-600)' : 'var(--c-text-3)' }}>
              {i + 1}
            </span>
          ))}
        </div>
        <div className="stage-track">
          {STAGES.map((_, i) => (
            <div
              key={i}
              className={`stage-seg ${i < contact.stage ? 'done' : i === contact.stage ? 'active' : ''}`}
              onClick={() => handleStageClick(i)}
              style={{ cursor: 'pointer' }}
            />
          ))}
        </div>
        <div className="stage-name">当前：{STAGES[contact.stage]}</div>
        <div style={{ display: 'flex', gap: 4, marginTop: 7, flexWrap: 'wrap' }}>
          {STAGES.map((s, i) => (
            <button
              key={i}
              onClick={() => handleStageClick(i)}
              style={{
                fontSize: 10, padding: '2px 8px', borderRadius: 10,
                border: '1px solid', cursor: 'pointer',
                background: i === contact.stage ? 'var(--c-blue-50)' : 'transparent',
                color: i === contact.stage ? 'var(--c-blue-600)' : 'var(--c-text-3)',
                borderColor: i === contact.stage ? 'var(--c-blue-100)' : 'var(--c-border-md)',
              }}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Tags */}
      <div className="crm-section">
        <div className="crm-section-title">标签</div>
        <div className="tags-wrap">
          {contact.tags.map((tag, i) => (
            <span key={tag} className={`tag ${TAG_COLORS[i % TAG_COLORS.length]}`}>
              {tag}
              <button className="tag-remove" onClick={() => handleRemoveTag(tag)} title="删除标签">
                <X size={10} />
              </button>
            </span>
          ))}
          {addingTag ? (
            <input
              className="tag-input"
              placeholder="新标签"
              value={tagText}
              autoFocus
              onChange={e => setTagText(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
                if (e.key === 'Escape') { setAddingTag(false); setTagText(''); }
              }}
              onBlur={() => {
                if (tagText.trim()) {
                  handleAddTag();
                } else {
                  setAddingTag(false);
                }
              }}
            />
          ) : (
            <button className="tag tag-gray tag-add" onClick={() => setAddingTag(true)}>
              <Plus size={10} />
              添加
            </button>
          )}
        </div>
      </div>

      {/* Orders */}
      <div className="crm-section">
        <div className="crm-section-title">历史订单</div>
        {contact.orders.length === 0 ? (
          <div className="empty-msg">暂无成交记录</div>
        ) : (
          contact.orders.map(o => (
            <div key={o.id} className="order-card">
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                <div>
                  <div className="order-title">{o.title}</div>
                  <div className="order-meta">{[o.amount, o.date].filter(Boolean).join(' · ') || '未填写金额和日期'}</div>
                </div>
                <button className="reminder-del" onClick={() => removeOrder(contact.id, o.id)} title="删除订单">
                  <X size={12} />
                </button>
              </div>
            </div>
          ))
        )}
        {addingOrder ? (
          <div className="inline-form">
            <input
              placeholder="订单标题"
              value={orderTitle}
              onChange={e => setOrderTitle(e.target.value)}
            />
            <input
              placeholder="金额，如 $12,400"
              value={orderAmount}
              onChange={e => setOrderAmount(e.target.value)}
            />
            <input
              placeholder="日期，如 2026-04-27"
              value={orderDate}
              onChange={e => setOrderDate(e.target.value)}
            />
            <div className="inline-actions">
              <button className="primary-mini" onClick={handleAddOrder}>确认</button>
              <button className="secondary-mini" onClick={() => setAddingOrder(false)}>取消</button>
            </div>
          </div>
        ) : (
          <button className="add-btn" onClick={() => setAddingOrder(true)}>
            <Plus size={12} />
            <span>添加订单记录</span>
          </button>
        )}
      </div>

      {/* Notes */}
      <div className="crm-section">
        <div className="crm-section-title">跟进备注</div>
        <textarea
          className="notes-textarea"
          value={draftNotes}
          onChange={handleNotesChange}
          onBlur={handleNotesBlur}
          placeholder="记录跟进情况..."
        />
      </div>

      {/* Reminders */}
      <div className="crm-section">
        <div className="crm-section-title">待办提醒</div>
        {contact.reminders.length === 0 && !addingReminder && (
          <div className="empty-msg">暂无提醒</div>
        )}
        {contact.reminders.map(r => (
          <div key={r.id} className="reminder-item">
            <div>
              <div className="reminder-text">{r.text}</div>
              <div className="reminder-time">{r.time}</div>
            </div>
            <button className="reminder-del" onClick={() => removeReminder(contact.id, r.id)}>
              <X size={12} />
            </button>
          </div>
        ))}
        {addingReminder && (
          <div className="inline-form">
            <input
              placeholder="提醒内容..."
              value={reminderText}
              onChange={e => setReminderText(e.target.value)}
            />
            <input
              placeholder="时间（如：明天 10:00）"
              value={reminderTime}
              onChange={e => setReminderTime(e.target.value)}
            />
            <div className="inline-actions">
              <button className="primary-mini" onClick={handleAddReminder}>确认</button>
              <button className="secondary-mini" onClick={() => { setAddingReminder(false); closeReminderComposer(); }}>取消</button>
            </div>
          </div>
        )}
        {!addingReminder && (
          <div className="add-btn" onClick={() => setAddingReminder(true)}>
            <Plus size={12} />
            <span>添加提醒</span>
          </div>
        )}
      </div>
    </div>
  );
}
